// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/MUI";

// Data
import axios from "axios";
import { withAuthContext } from "context/Auth";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import MDBadge from "components/MDBadge";
import UserIcon from "assets/images/bg-profile.jpeg";
import { useNavigate, useParams } from "react-router-dom";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import { withPropertyContext } from "context/Property";
import breakpoints from "assets/theme/base/breakpoints";
import { EmojiTransportation, House, SupervisedUserCircleOutlined } from "@mui/icons-material";
import ImageCloud from "../../link";
import MDButton from "components/MDButton";
import FilterAutocomplete from "components/FilterAutocomplete";
import { useCappedRetry } from "utils/retry";

function Tables({
	Token,
	RemovePropertyToUser,
	CheckToken,
	AdminRole,
	setUser,
	PropertyIdComplain
}) {
	const { propertyId } = useParams();
	const navigate = useNavigate();
	const retryGetUsers = useCappedRetry();
	const [data, setData] = useState([]);
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const isFetchingRef = useRef(false);
	const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [filters, setFilters] = useState({ verified: "", property: "", flatNo: "", search: "" });
	const [filterOptions, setFilterOptions] = useState({ types: [], verifiedStatuses: [], properties: [] });
	const [isFilterLoading, setIsFilterLoading] = useState(false);
	const [filterError, setFilterError] = useState("");

	const verifiedOptions = useMemo(() =>
		(filterOptions?.verifiedStatuses || []).map((status) => ({
			label: status ? "Verified" : "Pending",
			value: String(status)
		})),
	[filterOptions?.verifiedStatuses]
	);

	const propertyOptions = useMemo(() =>
		(filterOptions?.properties || []).map((property) => ({
			label: `${property?.Tower || ""} ${property?.FlatNo || ""}`.trim() || property?._id,
			value: property?._id
		})),
	[filterOptions?.properties]
	);

	const [tabsOrientation, setTabsOrientation] = useState("horizontal");
	const [tabValue, setTabValue] = useState("All");

	const columns = useMemo(() => [
		{
			headerName: "",
			field: "profilePicture",
			flex: 1,
			renderCell: ({ row: a }) => (
				<img
					src={
						a?.profilePicture?.filename
							? `${ImageCloud}/${a?.profilePicture?.filename}`
							: UserIcon
					}
					style={{
						width: "50px",
						height: "50px",
						borderRadius: "50px",
						border: "1px solid #000"
					}}
				/>
			)
		},
		{ headerName: "Name", field: "name", flex: 1 },
		{ headerName: "Email", field: "email", flex: 1 },
		{ headerName: "Type", field: "Type", flex: 1 },
		{ headerName: "Phone Number", field: "phoneNumber", flex: 1 },
		{
			headerName: "Property Description",
			field: "Property",
			flex: 1,
			valueGetter: ({ row: a }) => `${a?.Property?.description ? a?.Property?.description : "-"}
    `
		},
		{
			headerName: "Flat No",
			field: "flatNo",
			flex: 1,
			valueGetter: ({ row: a }) => `${a?.Property?.FlatNo ? a?.Property?.FlatNo : a?.flatNo}
    `
		},
		{ headerName: "Otp", field: "otp", flex: 1 },
		{ headerName: "Notifications", field: "notifications", flex: 1 },
		{
			headerName: "Action",
			field: "action",
			flex: 2,
			renderCell: ({ row: a }) => (
				<MDBox>
					{propertyId ? (
						<MDBadge
							badgeContent="Select User"
							color={"dark"}
							variant="gradient"
							size="sm"
							onClick={() => navigate(`/CarAction/${propertyId}/${a?._id}/New`)}
						/>
					) : setUser ? (
						<MDBadge
							badgeContent={"Select User"}
							color={"warning"}
							variant="gradient"
							size="sm"
							onClick={() => {
								setUser(a?._id);
							}}
						/>
					) : (
						<>
							{(AdminRole == "admin" || AdminRole == "superadmin" || AdminRole == "manager") && (
								<MDBadge
									badgeContent="View User"
									color={"dark"}
									variant="gradient"
									size="sm"
									onClick={() => navigate(`/UserAction/${a?.Property?._id || "none"}/${a?._id}`)}
								/>
							)}

							{!a?.Property?._id && (AdminRole == "admin" || AdminRole == "superadmin") ? (
								<MDBadge
									badgeContent="Add Property"
									color={"warning"}
									variant="gradient"
									size="sm"
									onClick={() => navigate(`/SelectProperty/${a?._id}`)}
								/>
							) : (
								(AdminRole == "admin" || AdminRole == "superadmin") && (
									<MDBadge
										badgeContent="Remove Property"
										color={"warning"}
										variant="gradient"
										size="sm"
										onClick={() =>
											RemovePropertyToUser({
												userId: a?._id,
												propertyId: a?.Property?._id,
												GetUsers
											})
										}
									/>
								)
							)}
						</>
					)}
				</MDBox>
			)
		}
	], [propertyId, setUser, AdminRole, navigate, RemovePropertyToUser]);

	const GetUsers = useCallback(() => {
		// Prevent duplicate API calls
		if (isFetchingRef.current) return;
		
		if (Token || localStorage.getItem("token")) {
			isFetchingRef.current = true;
			setIsLoading(true);
			const params = {
				page: currentPage,
				limit: itemsPerPage
			};
			if (filters.verified) params.verified = filters.verified;
			if (filters.property) params.property = filters.property;
			if (filters.flatNo) params.flatNo = filters.flatNo;
			if (filters.search) params.search = filters.search;

			axios
				.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetAllUsers/${tabValue}`, {
					params,
					headers: {
						Authorization: `Bearer ${Token || localStorage.getItem("accessToken") || localStorage.getItem("token")}`
					}
				})
				.then((res) => {
					if (res?.data?.status == 200) {
						let arr = res?.data?.data;
						if (PropertyIdComplain || propertyId) {
							arr = arr.filter((a) => {
								if (a?.Property?._id == (PropertyIdComplain || propertyId)) {
									return a;
								}
							});
						}
						setData(arr);
						if (res?.data?.pagination) {
							setPagination(res?.data?.pagination);
						}
					} else {
						setError(res?.data?.message);
					}
				})
				.catch((err) => {
					setError(err?.message);
				})
				.finally(() => {
					setIsLoading(false);
					isFetchingRef.current = false;
				});
		} else {
			retryGetUsers(() => {
				CheckToken();
				GetUsers();
			});
		}
	}, [Token, tabValue, PropertyIdComplain, propertyId, CheckToken, currentPage, itemsPerPage, filters]);

	const handleFilterChange = useCallback((field, value) => {
		setCurrentPage(1);
		setFilters((prev) => ({
			...prev,
			[field]: value || ""
		}));
	}, []);

	const handleClearFilters = useCallback(() => {
		setCurrentPage(1);
		setFilters({ verified: "", property: "", flatNo: "", search: "" });
	}, []);

	const fetchUserFilters = useCallback(() => {
		const tokenHeader = Token || localStorage.getItem("accessToken") || localStorage.getItem("token");
		if (!tokenHeader) return;
		setIsFilterLoading(true);
		setFilterError("");
		axios
			.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetUserFilters`, {
				headers: { Authorization: `Bearer ${tokenHeader}` }
			})
			.then((res) => {
				if (res?.data?.status === 200) {
					setFilterOptions(res?.data?.data || {});
				} else {
					setFilterError(res?.data?.message || "Unable to load filters");
				}
			})
			.catch((err) => {
				setFilterError(err?.response?.data?.message || err?.message || "Unable to load filters");
			})
			.finally(() => setIsFilterLoading(false));
	}, [Token]);

	useEffect(() => {
		fetchUserFilters();
	}, [fetchUserFilters]);

	const handleSetTabValue = useCallback((event, newValue) => {
		setTabValue(newValue);
	}, []);

	useEffect(() => {
		// A function that sets the orientation state of the tabs.
		function handleTabsOrientation() {
			return window.innerWidth < breakpoints.values.sm
				? setTabsOrientation("vertical")
				: setTabsOrientation("horizontal");
		}

		/** 
		 The event listener that's calling the handleTabsOrientation function when resizing the window.
		*/
		window.addEventListener("resize", handleTabsOrientation);

		// Call the handleTabsOrientation function to set the state with the initial value.
		handleTabsOrientation();

		// Remove event listener on cleanup
		return () => window.removeEventListener("resize", handleTabsOrientation);
	}, []);

	useEffect(() => {
		GetUsers();
	}, [GetUsers]);

	const handlePageChange = useCallback((newPage, newPageSize) => {
		if (newPageSize !== itemsPerPage) {
			setItemsPerPage(newPageSize);
			setCurrentPage(1);
		} else {
			setCurrentPage(newPage);
		}
	}, [itemsPerPage]);

	const Table = useMemo(
		() => () => (
			<Card>
				<MDBox
					px={2}
					py={2}
					mt={2}
					bgcolor="rgba(255,255,255,0.8)"
					borderRadius="md"
				>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} sm={6} md={3}>
							<FilterAutocomplete
								name="verified"
								label="Verified"
								value={filters.verified}
								options={verifiedOptions}
								onChange={handleFilterChange}
								loading={isFilterLoading}
								disabled={isFilterLoading}
								placeholder="Search verification state"
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<FilterAutocomplete
								name="property"
								label="Property"
								value={filters.property}
								options={propertyOptions}
								onChange={handleFilterChange}
								loading={isFilterLoading}
								disabled={isFilterLoading}
								placeholder="Search property"
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								label="Flat No"
								size="small"
								value={filters.flatNo}
								onChange={(e) => handleFilterChange("flatNo", e.target.value)}
								disabled={isFilterLoading}
							/>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								label="Search"
								size="small"
								value={filters.search}
								onChange={(e) => handleFilterChange("search", e.target.value)}
								disabled={isFilterLoading}
							/>
						</Grid>
						<Grid item xs={12} display="flex" justifyContent="flex-end">
							<MDButton
								variant="outlined"
								color="info"
								size="small"
								disabled={isFilterLoading}
								onClick={handleClearFilters}
							>
								Clear filters
							</MDButton>
						</Grid>
					</Grid>
					{filterError && (
						<MDTypography variant="caption" color="error">
							{filterError}
						</MDTypography>
					)}
				</MDBox>

				<MDBox
					mx={2}
					mt={-3}
					py={3}
					px={2}
					variant="gradient"
					bgColor="info"
					borderRadius="lg"
					coloredShadow="info"
				>
					<Grid container style={{ width: "100%" }}>
						<Grid item xs={6}>
							<MDTypography variant="h6" color="white">
								{tabValue == "rentant"
									? "Property Rentant Details"
									: tabValue == "owner"
										? "Property Owner Details"
										: "All Users Details"}
							</MDTypography>
						</Grid>
						{(AdminRole == "admin" || AdminRole == "superadmin" || AdminRole == "manager") &&
							!setUser && (
								<Grid
									item
									xs={6}
									style={{
										display: "flex",
										alignItems: "flex-end",
										alignContent: "flex-end",
										justifyContent: "flex-end"
									}}
								>
									<MDBadge
										badgeContent="New"
										color="dark"
										variant="gradient"
										size="sm"
										onClick={() => navigate(`/UserProperty/New`)}
									/>
								</Grid>
							)}
					</Grid>
				</MDBox>

				<MDBox
					mx={2}
					mt={-3}
					py={3}
					px={2}
					variant="gradient"
					bgColor="info"
					borderRadius="lg"
					coloredShadow="info"
				>
					<Grid
						container
						style={{
							width: "100%",
							alignItems: "flex-end",
							alignContent: "flex-end",
							justifyContent: "flex-end"
						}}
					>
						<Grid item md={6} xs={12}>
							<Tabs orientation={tabsOrientation} value={tabValue} onChange={handleSetTabValue}>
								<Tab
									label="All"
									value={"All"}
									icon={<SupervisedUserCircleOutlined fontSize="small" sx={{ mt: -0.25 }} />}
								/>
								<Tab
									label="Owners"
									value={"owner"}
									icon={<EmojiTransportation fontSize="small" sx={{ mt: -0.25 }} />}
								/>
								<Tab
									label="Rentants"
									value={"rentant"}
									icon={<House fontSize="small" sx={{ mt: -0.25 }} />}
								/>
							</Tabs>
						</Grid>
					</Grid>
				</MDBox>

				<MDBox pt={3}>

					{isLoading ? (
						<MDBox lineHeight={1} textAlign="center" py={3}>
							<MDTypography display="block" variant="caption" color="text" fontWeight="medium" fontSize="16px">
								Loading...
							</MDTypography>
						</MDBox>
					) : data.length > 0 ? (
						<DataTable 
							rows={data} 
							columns={columns} 
							paginationMode="server"
							serverPagination={{
								total: pagination?.total || 0,
								page: currentPage,
								limit: itemsPerPage,
								onPageChange: handlePageChange
							}}
						/>
					) : (
						<MDBox lineHeight={1} textAlign="center">
							<MDTypography
								display="block"
								variant="caption"
								color="text"
								fontWeight="medium"
								fontSize="20px"
							>
								{error ? error : " No Data Found"}
							</MDTypography>
						</MDBox>
					)}
				</MDBox>
			</Card>
		),
		[data, error, tabValue, tabsOrientation, AdminRole, navigate, propertyId, setUser, isLoading, handleSetTabValue, columns, pagination, currentPage, itemsPerPage, handlePageChange, filters, filterOptions, isFilterLoading, filterError, handleFilterChange, handleClearFilters]
	);

	if (setUser) {
		return <Table />;
	} else {
		return (
			<DashboardLayout>
				<DashboardNavbar />
				<MDBox pt={6} pb={3}>
					<Grid container spacing={6}>
						<Grid item xs={12}>
							<Table />
						</Grid>
					</Grid>
				</MDBox>
			</DashboardLayout>
		);
	}
}

export default withAuthContext(withPropertyContext(Tables));
